import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { menuAPI, MenuItem } from "../../../services/menuAPI";
import { useAuthStore } from "../../../store/authStore";
import { useActiveTenantId } from "../../../store/tenantStore";

interface SidebarMenuProps {
  toggleActive: () => void;
}

const DynamicSidebarMenu: React.FC<SidebarMenuProps> = ({ toggleActive }) => {
  const { pathname } = useLocation();
  
  // Tenant filter: use store so menu refetches when TenantSelector changes tenant
  const storeTenantId = useActiveTenantId();
  const fallback = (() => {
    const r = typeof window !== 'undefined' ? localStorage.getItem('activeTenantId') : null;
    if (!r) return 1;
    const n = parseInt(r, 10);
    return Number.isNaN(n) ? 1 : n;
  })();
  const tenantId = storeTenantId ?? fallback;

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Fetch menus with React Query (cache for 1 hour) only when authenticated
  const { data: menus = [], isLoading, error } = useQuery<MenuItem[]>({
    queryKey: ['menus-tree', tenantId],
    queryFn: () => menuAPI.getTree(tenantId, true), // Get only active menus (tree)
    enabled: !!isAuthenticated,
    staleTime: 3600000, // 1 hour
    gcTime: 3600000, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  // Track which menu items are expanded
  const [expandedMenus, setExpandedMenus] = useState<Set<number>>(new Set());

  const toggleMenu = (menuId: number) => {
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(menuId)) {
        newSet.delete(menuId);
      } else {
        newSet.add(menuId);
      }
      return newSet;
    });
  };

  // Auto-expand menu if current path matches
  useEffect(() => {
    const findAndExpandMenu = (items: MenuItem[], parentId?: number) => {
      for (const item of items) {
        if (pathname === item.route || pathname.startsWith(item.route + '/')) {
          if (parentId) {
            setExpandedMenus((prev) => new Set(prev).add(parentId));
          }
          return true;
        }
        if (item.children && item.children.length > 0) {
          if (findAndExpandMenu(item.children, item.id)) {
            setExpandedMenus((prev) => new Set(prev).add(item.id));
            return true;
          }
        }
      }
      return false;
    };
    
    if (menus.length > 0) {
      findAndExpandMenu(menus);
    }
  }, [pathname, menus]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);

  // Render menu icon (Material Symbols)
  const renderIcon = (iconName?: string) => {
    if (!iconName) return null;
    
    // Map common icon names to Material Symbols
    const iconMap: Record<string, string> = {
      // Backward/compat: some backends may send lucide-style or custom names
      'layout-dashboard': 'dashboard',
      'building2': 'business',
      'settings2': 'settings',
      'share2': 'share',

      'dashboard': 'dashboard',
      'users': 'people',
      'user': 'person',
      'building-2': 'business',
      'building': 'apartment',
      'settings': 'settings',
      'shield': 'shield',
      'menu': 'menu',
      'briefcase': 'work',
      'map-pin': 'place',
      'home': 'home',
      'bar-chart-3': 'bar_chart',
      'file-text': 'description',
      'calendar': 'calendar_today',
      'mail': 'mail',
      'bell': 'notifications',
      'search': 'search',
    };
    
    const materialIcon = iconMap[iconName.toLowerCase()] || iconName.toLowerCase();
    
    return (
      <i className="material-symbols-outlined transition-all text-gray-500 dark:text-gray-400 ltr:mr-[7px] rtl:ml-[7px] !text-[22px] leading-none relative -top-px">
        {materialIcon}
      </i>
    );
  };

  // Padding by level (Tailwind-safe: level 0 = 14px, 1 = 38px, 2 = 58px)
  const paddingByLevel = ['ltr:pl-[14px] rtl:pr-[14px]', 'ltr:pl-[38px] rtl:pr-[38px]', 'ltr:pl-[58px] rtl:pr-[58px]'];
  const pad = (l: number) => paddingByLevel[Math.min(l, 2)] ?? paddingByLevel[2];

  // Render menu item recursively (supports nested levels: Sistem Ayarları -> Veri Şablonları -> Kuruluş Tipleri / NACE Kodları)
  const renderMenuItem = (menu: MenuItem, index: number, level: number = 0) => {
    const activeChildren = menu.children?.filter((child: MenuItem) => child.isActive) || [];
    const hasActiveChildren = activeChildren.length > 0;
    const isExpanded = expandedMenus.has(menu.id);
    const isActive = pathname === menu.route || pathname.startsWith(menu.route + '/');

    if (hasActiveChildren) {
      return (
        <div key={menu.id} className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
          <button
            className={`accordion-button toggle flex items-center transition-all py-[9px] ${pad(level)} ltr:pr-[30px] rtl:pl-[30px] rounded-md font-medium w-full relative hover:bg-gray-50 text-left dark:hover:bg-[#15203c] ${
              isExpanded ? "open" : ""
            }`}
            type="button"
            onClick={() => toggleMenu(menu.id)}
          >
            {renderIcon(menu.icon)}
            <span className="title leading-none">{menu.label}</span>
          </button>

          <div className={`accordion-collapse ${isExpanded ? "open" : "hidden"}`}>
            <div className="pt-[4px]">
              <ul className="sidebar-sub-menu">
                {activeChildren
                  .sort((a: MenuItem, b: MenuItem) => a.sortOrder - b.sortOrder)
                  .map((child: MenuItem, childIndex: number) => (
                    <li key={child.id} className="sidemenu-item mb-[4px] last:mb-0">
                      {renderMenuItem(child, childIndex, level + 1)}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div key={menu.id} className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
          <Link
            to={menu.route}
            className={`accordion-button flex items-center transition-all py-[9px] ${pad(level)} ltr:pr-[30px] rtl:pl-[30px] rounded-md font-medium w-full relative hover:bg-gray-50 text-left dark:hover:bg-[#15203c] ${
              isActive ? "active" : ""
            }`}
          >
            {renderIcon(menu.icon)}
            <span className="title leading-none">{menu.label}</span>
          </Link>
        </div>
      );
    }
  };

  return (
    <>
      <div className="sidebar-area bg-white dark:bg-[#0c1427] fixed z-[7] top-0 h-screen transition-all rounded-r-md">
        <div className="logo bg-white dark:bg-[#0c1427] border-b border-gray-100 dark:border-[#172036] px-[25px] pt-[19px] pb-[15px] absolute z-[2] right-0 top-0 left-0">
          <Link
            to="/dashboard"
            className="transition-none relative flex items-center outline-none"
          >
            <img
              src="/images/logo-icon.svg"
              alt="logo-icon"
              width={26}
              height={26}
            />
            <span className="font-bold text-black dark:text-white relative ltr:ml-[8px] rtl:mr-[8px] top-px text-xl">
              BIArtHR
            </span>
          </Link>

          <button
            type="button"
            className="burger-menu inline-block absolute z-[3] top-[24px] ltr:right-[25px] rtl:left-[25px] transition-all hover:text-primary-500"
            onClick={toggleActive}
          >
            <i className="material-symbols-outlined">close</i>
          </button>
        </div>

        <div className="pt-[89px] px-[22px] pb-[20px] h-screen overflow-y-scroll sidebar-custom-scrollbar">
          <div className="accordion">
            <span className="block relative font-medium uppercase text-gray-400 mb-[8px] text-xs">
              Main
            </span>

            {isLoading && (
              <div className="px-4 py-2 text-xs text-gray-400">
                Menüler yükleniyor...
              </div>
            )}

            {error && (
              <div className="px-4 py-2 text-xs text-red-400">
                Menüler yüklenirken hata oluştu
              </div>
            )}

            {!isLoading && !error && menus.length === 0 && (
              <div className="px-4 py-2 text-xs text-gray-400">
                Menü bulunamadı
              </div>
            )}

            {!isLoading && !error && menus.length > 0 && (
              <>
                {menus
                  .filter((menu) => menu.isActive)
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((menu, index) => renderMenuItem(menu, index))}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DynamicSidebarMenu;
